/* eslint-disable @typescript-eslint/no-unused-vars */
import { useContext, useCallback } from "react";
import UserAuthStateContext from "../_contexts/userAuth.context";
import { LOGIN, LOGOUT } from "../_models/types";
import { jwtDecode } from "jwt-decode";
import { ACCESS_TOKEN_TIME } from "../_constants";

const api = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

const REFRESH_BUFFER = 10;

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

let lastRefreshAt = 0;

const useAPI = () => {
  const { UserAuthState, dispatchUserAuth } = useContext(UserAuthStateContext);

  const handleTokenRefresh = useCallback(async (): Promise<string | null> => {
    const now = Date.now();

    // ⏸ prevent refreshing again if done within the last 2 minutes
    if (now - lastRefreshAt < 2 * 60 * 1000) {
      const stored = JSON.parse(localStorage.getItem("_artw") || "{}");
      return stored?.access || null;
    }

    if (isRefreshing && refreshPromise) {
      return refreshPromise;
    }

    isRefreshing = true;
    refreshPromise = (async () => {
      try {
        const stored = JSON.parse(localStorage.getItem("_artw") || "{}");
        if (!stored?.refresh) {
          isRefreshing = false;
          refreshPromise = null;
          return null;
        }

        // skip if still valid
        const now = Date.now();
        if (stored.expiry && now < stored.expiry - REFRESH_BUFFER * 1000) {
          isRefreshing = false;
          refreshPromise = null;
          return stored.access;
        }

        const res = await fetch(`${api}/api/token/refresh/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh: stored.refresh }),
        });

        const data = await res.json();

        if (!res.ok || !data.access) {
          dispatchUserAuth({ type: LOGOUT });
          localStorage.removeItem("_artw");
          isRefreshing = false;
          refreshPromise = null;
          return null;
        }

        const decoded: any = jwtDecode(data.access);
        const newExpiry = decoded.exp * 1000;

        const updated = {
          ...stored,
          access: data.access,
          expiry: newExpiry,
          refresh: data.refresh || stored.refresh,
        };

        localStorage.setItem("_artw", JSON.stringify(updated));
        dispatchUserAuth({ type: LOGIN, payload: updated });

        lastRefreshAt = Date.now();

        isRefreshing = false;
        refreshPromise = null;
        return data.access;
      } catch {
        isRefreshing = false;
        refreshPromise = null;
        return null;
      }
    })();

    return refreshPromise;
  }, [dispatchUserAuth]);

  const authToken = useCallback(async (): Promise<string | null> => {
    let token = UserAuthState?.token;
    const stored = JSON.parse(localStorage.getItem("_artw") || "{}");
    if (!token) token = stored?.access || null;
    if (!token) return null;

    try {
      const decoded: any = jwtDecode(token);
      const now = Math.floor(Date.now() / 1000);
      const timeLeft = decoded.exp - now;

      if (timeLeft <= REFRESH_BUFFER) {
        const newToken = await handleTokenRefresh();
        return newToken || token;
      }

      return token;
    } catch {
      return null;
    }
  }, [UserAuthState, handleTokenRefresh]);

  const handleResponse = async (response: Response) => {
    let data: any = null;
    try {
      const text = await response.text();
      data = text ? JSON.parse(text) : null;
    } catch {
      // ignore parse errors for empty responses
    }

    if (!response.ok) {
      if (response.status === 401) {
        dispatchUserAuth({ type: LOGOUT });
        localStorage.removeItem("_artw");
      }
      const error = (data && (data.detail ?? data)) || response.statusText;
      return Promise.reject(error);
    }
    return data;
  };

  const request = useCallback(
    (method: string) => async (url: string, body?: any) => {
      const token = await authToken();
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const requestOptions: RequestInit = { method, headers };
      if (body) requestOptions.body = JSON.stringify(body);

      return fetch(url, requestOptions).then(handleResponse);
    },
    [authToken]
  );

  return {
    get: request("GET"),
    post: request("POST"),
    patch: request("PATCH"),
    put: request("PUT"),
    delete: request("DELETE"),
  };
};

export default useAPI;
